import { useCallback, useState } from 'react'
import { reverseGeocode } from '../api/geocode'
import { LOCATION_DETECT_ERRORS, type DeliveryLocation } from '../constants/location'

type DetectStatus = 'idle' | 'loading' | 'error'

type DetectResult =
  | { ok: true; location: DeliveryLocation }
  | { ok: false; message: string }

function readPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15_000,
      maximumAge: 30_000,
    })
  })
}

export function useDetectDeliveryCity() {
  const [status, setStatus] = useState<DetectStatus>('idle')
  const [error, setError] = useState('')

  const detect = useCallback(async (): Promise<DeliveryLocation | null> => {
    setStatus('loading')
    setError('')

    const result = await detectLocation()
    if (!result.ok) {
      setStatus('error')
      setError(result.message)
      return null
    }

    setStatus('idle')
    return result.location
  }, [])

  const clearError = useCallback(() => {
    setError('')
    setStatus('idle')
  }, [])

  return {
    status,
    error,
    isDetecting: status === 'loading',
    detect,
    clearError,
  }
}

async function detectLocation(): Promise<DetectResult> {
  if (!navigator.geolocation) {
    return { ok: false, message: LOCATION_DETECT_ERRORS.unsupported }
  }

  try {
    const position = await readPosition()
    const { latitude, longitude } = position.coords
    const location = await reverseGeocode(latitude, longitude)
    if (!location) {
      return { ok: false, message: LOCATION_DETECT_ERRORS.geocodeFailed }
    }
    return { ok: true, location }
  } catch (err) {
    const code =
      err && typeof err === 'object' && 'code' in err
        ? Number((err as GeolocationPositionError).code)
        : 0
    if (code === 1) {
      return { ok: false, message: LOCATION_DETECT_ERRORS.denied }
    }
    if (code === 2 || code === 3) {
      return { ok: false, message: LOCATION_DETECT_ERRORS.unavailable }
    }
    return { ok: false, message: LOCATION_DETECT_ERRORS.geocodeFailed }
  }
}
